/**
 * useAIInterview Hook
 *
 * Manages AI Interview session state, SSE streaming, and entity tracking.
 */

import { useState, useCallback } from 'react'
import { useAIStore } from '@/stores/aiStore'
import {
  aiInterviewApi,
  type AIInterviewStreamEvent,
} from '@/lib/api/client'
import { stripMetadataBlock } from '@/lib/utils/stripMetadataBlock'
import type {
  AIInterviewSession,
  AIInterviewMessage,
  AIInterviewPlan,
  LiveExtractedEntity,
  AIInterviewInputMethod,
} from '@ispot/shared'

// ============================================================================
// TYPES
// ============================================================================

export interface AIInterviewState {
  session: AIInterviewSession | null
  messages: AIInterviewMessage[]
  plan: AIInterviewPlan | null
  entities: LiveExtractedEntity[]
  mcqOptions: string[]
  isLoading: boolean
  isStreaming: boolean
  isPlanGenerating: boolean
  error: string | null
}

// ============================================================================
// HOOK
// ============================================================================

export function useAIInterview(accountId: string, projectId: string) {
  const [state, setState] = useState<AIInterviewState>({
    session: null,
    messages: [],
    plan: null,
    entities: [],
    mcqOptions: [],
    isLoading: false,
    isStreaming: false,
    isPlanGenerating: false,
    error: null,
  })

  const { apiKey } = useAIStore()

  const createSession = useCallback(async (data: {
    researchGoal: string
    interviewType: string
    linkedPersonName: string
    focusTopics?: string[]
    title?: string
  }) => {
    setState(s => ({ ...s, isLoading: true, error: null }))
    try {
      const session = await aiInterviewApi.createSession(accountId, projectId, data)
      setState(s => ({ ...s, session, isLoading: false }))
      return session
    } catch (error) {
      setState(s => ({ ...s, isLoading: false, error: error instanceof Error ? error.message : 'Failed to create session' }))
      return null
    }
  }, [accountId, projectId])

  const loadSession = useCallback(async (sessionId: string) => {
    setState(s => ({ ...s, isLoading: true, error: null }))
    try {
      const data = await aiInterviewApi.getSession(accountId, projectId, sessionId) as AIInterviewSession & { messages: AIInterviewMessage[] }
      const allEntities = data.messages
        .flatMap(m => m.extractedEntities || [])
      setState(s => ({
        ...s,
        session: data,
        messages: data.messages || [],
        plan: data.plan || null,
        entities: allEntities,
        isLoading: false,
      }))
    } catch (error) {
      setState(s => ({ ...s, isLoading: false, error: error instanceof Error ? error.message : 'Failed to load session' }))
    }
  }, [accountId, projectId])

  const reloadSession = useCallback(async (sessionId: string) => {
    try {
      const data = await aiInterviewApi.getSession(accountId, projectId, sessionId) as AIInterviewSession & { messages: AIInterviewMessage[] }
      const allEntities = data.messages.flatMap(m => m.extractedEntities || [])
      setState(s => ({
        ...s,
        session: data,
        messages: data.messages || [],
        plan: data.plan || s.plan,
        entities: allEntities.length > 0 ? allEntities : s.entities,
        isPlanGenerating: false,
      }))
    } catch {
      // Non-critical: SSE state is primary, DB reload is a fallback
    }
  }, [accountId, projectId])

  const generatePlan = useCallback(async (sessionId: string) => {
    if (!apiKey) {
      setState(s => ({ ...s, error: 'API key not configured' }))
      return
    }

    setState(s => ({ ...s, isPlanGenerating: true, error: null }))

    try {
      await aiInterviewApi.generatePlanStream(
        accountId,
        projectId,
        sessionId,
        { apiKey },
        (event: AIInterviewStreamEvent) => {
          if (event.type === 'plan') {
            const plan = (event as { plan: AIInterviewPlan }).plan
            setState(s => ({ ...s, plan, session: s.session ? { ...s.session, plan, status: 'active' } : null }))
          } else if (event.type === 'text') {
            const content = (event as { content: string }).content
            const aiMessage: AIInterviewMessage = {
              id: crypto.randomUUID(),
              sessionId,
              role: 'ai',
              content: stripMetadataBlock(content),
              inputMethod: null,
              questionIntent: null,
              extractedEntities: [],
              mcqOptions: null,
              selectedMcqOption: null,
              createdAt: new Date().toISOString(),
            }
            setState(s => ({ ...s, messages: [...s.messages, aiMessage] }))
          } else if (event.type === 'metadata') {
            const mcqOptions = (event as { mcqOptions?: string[] }).mcqOptions || []
            setState(s => ({ ...s, mcqOptions }))
          } else if (event.type === 'done') {
            setState(s => ({ ...s, isPlanGenerating: false }))
          } else if (event.type === 'error') {
            setState(s => ({ ...s, isPlanGenerating: false, error: (event as { message: string }).message }))
          }
        },
      )

      // Reload session from DB to ensure messages are populated
      // (fallback in case SSE events were missed during streaming)
      await reloadSession(sessionId)
    } catch (error) {
      setState(s => ({ ...s, isPlanGenerating: false, error: error instanceof Error ? error.message : 'Plan generation failed' }))
    }
  }, [accountId, projectId, apiKey, reloadSession])

  const sendMessage = useCallback(async (
    content: string,
    inputMethod: AIInterviewInputMethod = 'text',
    selectedMcqOption?: string,
  ) => {
    if (!state.session || !apiKey) return

    const sessionId = state.session.id

    // Add user message optimistically
    const userMessage: AIInterviewMessage = {
      id: crypto.randomUUID(),
      sessionId,
      role: 'user',
      content,
      inputMethod,
      questionIntent: null,
      extractedEntities: [],
      mcqOptions: null,
      selectedMcqOption: selectedMcqOption || null,
      createdAt: new Date().toISOString(),
    }

    // Add placeholder AI message
    const placeholderMessage: AIInterviewMessage = {
      id: 'streaming',
      sessionId,
      role: 'ai',
      content: '',
      inputMethod: null,
      questionIntent: null,
      extractedEntities: [],
      mcqOptions: null,
      selectedMcqOption: null,
      createdAt: new Date().toISOString(),
    }

    setState(s => ({
      ...s,
      messages: [...s.messages, userMessage, placeholderMessage],
      mcqOptions: [],
      isStreaming: true,
      error: null,
    }))

    try {
      await aiInterviewApi.chatStream(
        accountId,
        projectId,
        sessionId,
        { content, apiKey, inputMethod, selectedMcqOption },
        (event: AIInterviewStreamEvent) => {
          if (event.type === 'text') {
            const text = (event as { content: string }).content
            setState(s => ({
              ...s,
              messages: s.messages.map(m =>
                m.id === 'streaming'
                  ? { ...m, content: stripMetadataBlock(text) }
                  : m
              ),
            }))
          } else if (event.type === 'entities') {
            const newEntities = (event as { entities: LiveExtractedEntity[] }).entities
            setState(s => ({ ...s, entities: [...s.entities, ...newEntities] }))
          } else if (event.type === 'metadata') {
            const mcqOptions = (event as { mcqOptions?: string[] }).mcqOptions || []
            setState(s => ({ ...s, mcqOptions }))
          } else if (event.type === 'done') {
            setState(s => ({
              ...s,
              isStreaming: false,
              messages: s.messages.map(m =>
                m.id === 'streaming' ? { ...m, id: crypto.randomUUID() } : m
              ),
            }))
          } else if (event.type === 'error') {
            setState(s => ({
              ...s,
              isStreaming: false,
              error: (event as { message: string }).message,
              messages: s.messages.filter(m => m.id !== 'streaming'),
            }))
          }
        },
      )
    } catch (error) {
      setState(s => ({
        ...s,
        isStreaming: false,
        error: error instanceof Error ? error.message : 'Message failed',
        messages: s.messages.filter(m => m.id !== 'streaming'),
      }))
    }
  }, [state.session, apiKey, accountId, projectId])

  const completeSession = useCallback(async () => {
    if (!state.session) return null
    setState(s => ({ ...s, isLoading: true }))
    try {
      const result = await aiInterviewApi.completeSession(accountId, projectId, state.session.id)
      setState(s => ({
        ...s,
        isLoading: false,
        session: s.session ? { ...s.session, status: 'completed' } : null,
      }))
      return result
    } catch (error) {
      setState(s => ({ ...s, isLoading: false, error: error instanceof Error ? error.message : 'Failed to complete' }))
      return null
    }
  }, [state.session, accountId, projectId])

  return {
    ...state,
    createSession,
    loadSession,
    generatePlan,
    sendMessage,
    completeSession,
    isConfigured: !!apiKey,
  }
}
